import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { api, ASSET_TYPE_LABELS } from '../utils/api'
import { ArrowLeftRight, Plus, Check, X, Clock, ChevronLeft, ChevronRight, Trash2, Calendar, History, PlusCircle, CheckCircle2, XCircle, RotateCcw, CalendarClock } from 'lucide-react'
import { AssetSearchSelect } from './TicketsPage'

interface BookingLogEntry {
  id: string; action: string; oldValue?: string; newValue?: string; actor?: string; createdAt: string
}
interface Booking {
  id: string; assetId?: string; itemName?: string; startDate: string; endDate: string
  purpose?: string; bookedBy?: string; notes?: string; status: string
  createdAt?: string
  approvedBy?: string; approvedAt?: string
  rejectedBy?: string; rejectedAt?: string
  returnedBy?: string; returnedAt?: string
  logs?: BookingLogEntry[]
  asset?: { id: string; name: string; assetCode?: string; type: string }
}

const STATUS_COLOR: Record<string, string> = {
  PENDING:   'bg-yellow-50 text-yellow-700 border-yellow-200',
  APPROVED:  'bg-green-50 text-green-700 border-green-200',
  REJECTED:  'bg-red-50 text-red-700 border-red-200',
  RETURNED:  'bg-blue-50 text-blue-700 border-blue-200',
  CANCELLED: 'bg-gray-50 text-gray-500 border-gray-200',
}
const STATUS_LABEL: Record<string, string> = {
  PENDING:'รอดำเนินการ', APPROVED:'กำลังยืม', REJECTED:'ปฏิเสธ', RETURNED:'คืนแล้ว', CANCELLED:'ยกเลิก'
}

interface NewBookingForm {
  assetId: string; startDate: string; endDate: string
  startTime: string; endTime: string
  purpose: string; bookedBy: string; notes: string
  useCustomItem: boolean; itemName: string
}
const emptyForm: NewBookingForm = { assetId:'', startDate:'', endDate:'', startTime:'08:00', endTime:'17:00', purpose:'', bookedBy:'', notes:'', useCustomItem:false, itemName:'' }

export default function BookingsPage({ isAdmin }: { isAdmin: boolean }) {
  const qc = useQueryClient()
  const now = new Date()
  const [viewMonth, setViewMonth] = useState(now.getMonth()+1)
  const [viewYear,  setViewYear]  = useState(now.getFullYear())
  const [showForm,  setShowForm]  = useState(false)
  const [form,      setForm]      = useState<NewBookingForm>(emptyForm)
  const [conflict,  setConflict]  = useState<string|null>(null)
  const [view, setView] = useState<'calendar'|'list'>('list')
  const [extendTarget, setExtendTarget] = useState<Booking|null>(null)
  const [extendDate,   setExtendDate]   = useState('')
  const [extendTime,   setExtendTime]   = useState('17:00')
  const [extendError,  setExtendError]  = useState<string|null>(null)
  const [expandedHistory, setExpandedHistory] = useState<Set<string>>(new Set())
  const toggleHistory = (id: string) => setExpandedHistory(s => {
    const next = new Set(s)
    if (next.has(id)) next.delete(id); else next.add(id)
    return next
  })

  const { data: bookings = [] } = useQuery<Booking[]>({
    queryKey: ['bookings', viewMonth, viewYear],
    queryFn: () => api.get(`/bookings/calendar?month=${viewMonth}&year=${viewYear}`).then(r=>r.data),
  })

  const createMut = useMutation({
    mutationFn: async (f: NewBookingForm) => {
      const startDate = `${f.startDate}T${f.startTime}:00`
      const endDate   = `${f.endDate}T${f.endTime}:00`
      if (!f.useCustomItem) {
        const check = await api.post('/bookings/check', { assetId: f.assetId, startDate, endDate })
        if (!check.data.available) { setConflict('ช่วงเวลานี้มีการยืมอยู่แล้ว'); return }
      }
      return api.post('/bookings', {
        assetId: f.useCustomItem ? undefined : f.assetId,
        itemName: f.useCustomItem ? f.itemName : undefined,
        startDate, endDate, purpose: f.purpose, bookedBy: f.bookedBy, notes: f.notes,
      })
    },
    onSuccess: (r) => { if (r) { qc.invalidateQueries({queryKey:['bookings']}); setShowForm(false); setForm(emptyForm); setConflict(null) } },
  })

  const statusMut = useMutation({
    mutationFn: ({id,status}:{id:string;status:string}) => api.patch(`/bookings/${id}/status`, {status}),
    onSuccess: () => qc.invalidateQueries({queryKey:['bookings']}),
  })
  const deleteMut = useMutation({
    mutationFn: (id: string) => api.delete(`/bookings/${id}`),
    onSuccess: () => qc.invalidateQueries({queryKey:['bookings']}),
  })
  const extendMut = useMutation({
    mutationFn: ({id,endDate}:{id:string;endDate:string}) => api.patch(`/bookings/${id}/extend`, {endDate}),
    onSuccess: () => { qc.invalidateQueries({queryKey:['bookings']}); setExtendTarget(null); setExtendError(null) },
    onError: (e: any) => setExtendError(e?.response?.data?.message || 'ไม่สามารถขยายเวลาได้'),
  })

  const openExtend = (b: Booking) => {
    const d = new Date(b.endDate)
    const pad = (n: number) => String(n).padStart(2,'0')
    setExtendDate(`${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}`)
    setExtendTime(`${pad(d.getHours())}:${pad(d.getMinutes())}`)
    setExtendError(null)
    setExtendTarget(b)
  }

  const fmtDT = (d: string) => {
    const dt = new Date(d)
    return `${dt.toLocaleDateString('th-TH')} ${dt.toLocaleTimeString('th-TH',{hour:'2-digit',minute:'2-digit'})}`
  }

  const prevMonth = () => { if (viewMonth===1) { setViewMonth(12); setViewYear(y=>y-1) } else setViewMonth(m=>m-1) }
  const nextMonth = () => { if (viewMonth===12) { setViewMonth(1);  setViewYear(y=>y+1) } else setViewMonth(m=>m+1) }

  // Calendar grid
  const daysInMonth = new Date(viewYear, viewMonth, 0).getDate()
  const firstDay    = new Date(viewYear, viewMonth-1, 1).getDay()
  const ic = "w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-5">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2"><ArrowLeftRight size={20}/> การยืม Asset</h1>
        <div className="flex items-center gap-2">
          <div className="flex bg-gray-100 rounded-lg p-1">
            <button onClick={()=>setView('list')} className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all ${view==='list'?'bg-white shadow-sm text-gray-900':'text-gray-500'}`}>รายการ</button>
            <button onClick={()=>setView('calendar')} className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all ${view==='calendar'?'bg-white shadow-sm text-gray-900':'text-gray-500'}`}>ปฏิทิน</button>
          </div>
          <button onClick={() => setShowForm(true)} className="flex items-center gap-1.5 px-4 py-2 bg-blue-600 text-white rounded-xl text-sm font-medium hover:bg-blue-700">
            <Plus size={16}/> ยืม Asset
          </button>
        </div>
      </div>

      {/* Month nav */}
      <div className="flex items-center gap-3">
        <button onClick={prevMonth} className="p-2 hover:bg-gray-100 rounded-lg"><ChevronLeft size={18}/></button>
        <span className="font-semibold text-gray-800 min-w-[140px] text-center">
          {new Date(viewYear, viewMonth-1).toLocaleDateString('th-TH',{month:'long',year:'numeric'})}
        </span>
        <button onClick={nextMonth} className="p-2 hover:bg-gray-100 rounded-lg"><ChevronRight size={18}/></button>
        <span className="text-sm text-gray-500">{bookings.length} การยืม</span>
      </div>

      {/* Calendar view */}
      {view === 'calendar' && (
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="grid grid-cols-7 border-b border-gray-100">
            {['อา','จ','อ','พ','พฤ','ศ','ส'].map(d => (
              <div key={d} className="p-2 text-center text-xs font-semibold text-gray-500 uppercase">{d}</div>
            ))}
          </div>
          <div className="grid grid-cols-7">
            {Array.from({length: firstDay}).map((_,i) => <div key={`empty-${i}`} className="p-2 min-h-[80px] border-b border-r border-gray-50"/>)}
            {Array.from({length: daysInMonth}).map((_,i) => {
              const day = i+1
              const dayBookings = bookings.filter(b => {
                const s = new Date(b.startDate); const e = new Date(b.endDate)
                const d = new Date(viewYear, viewMonth-1, day)
                return d >= new Date(s.getFullYear(),s.getMonth(),s.getDate()) && d <= new Date(e.getFullYear(),e.getMonth(),e.getDate())
              })
              const isToday = new Date().getDate()===day && new Date().getMonth()+1===viewMonth && new Date().getFullYear()===viewYear
              return (
                <div key={day} className={`p-2 min-h-[80px] border-b border-r border-gray-50 ${isToday?'bg-blue-50':''}`}>
                  <span className={`text-xs font-medium ${isToday?'bg-blue-600 text-white w-5 h-5 rounded-full flex items-center justify-center':'text-gray-600'}`}>{day}</span>
                  {dayBookings.slice(0,2).map(b => (
                    <div key={b.id} className={`mt-1 text-xs px-1.5 py-0.5 rounded truncate border ${STATUS_COLOR[b.status]}`}>
                      {b.asset?.name||b.itemName||b.assetId}
                    </div>
                  ))}
                  {dayBookings.length > 2 && <div className="text-xs text-gray-400 mt-0.5">+{dayBookings.length-2} เพิ่มเติม</div>}
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* List view */}
      {view === 'list' && (
        <div className="space-y-3">
          {bookings.length === 0 && (
            <div className="bg-white rounded-xl border border-gray-100 p-12 text-center">
              <Calendar size={40} className="mx-auto text-gray-200 mb-3"/>
              <p className="text-gray-400">ยังไม่มีการยืมในเดือนนี้</p>
            </div>
          )}
          {bookings.map(b => (
            <div key={b.id} className="bg-white rounded-xl border border-gray-100 shadow-sm p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium border ${STATUS_COLOR[b.status]}`}>{STATUS_LABEL[b.status]}</span>
                    {b.asset && <Link to={`/assets/${b.asset.id}`} className="text-sm font-semibold text-blue-600 hover:underline">{b.asset.assetCode||b.asset.name}</Link>}
                    {b.asset && <span className="text-xs text-gray-400">{ASSET_TYPE_LABELS[b.asset.type as any]}</span>}
                    {!b.asset && b.itemName && <span className="text-sm font-semibold text-gray-800">{b.itemName}</span>}
                  </div>
                  <div className="flex items-center gap-1.5 text-sm text-gray-600">
                    <Clock size={13}/> {fmtDT(b.startDate)} – {fmtDT(b.endDate)}
                  </div>
                  {b.purpose && <p className="text-sm text-gray-700 mt-1">📌 {b.purpose}</p>}
                  {b.bookedBy && <p className="text-xs text-gray-500 mt-0.5">ผู้ยืม: {b.bookedBy}</p>}
                  {b.notes && <p className="text-xs text-gray-400 mt-0.5">{b.notes}</p>}
                  <button onClick={() => toggleHistory(b.id)} className="flex items-center gap-1 text-xs text-gray-400 hover:text-blue-600 mt-1.5">
                    <History size={12}/> {expandedHistory.has(b.id) ? 'ซ่อนประวัติ' : 'ดูประวัติ'}
                  </button>
                  {expandedHistory.has(b.id) && (() => {
                    type HistItem = { key: string; at: string; icon: JSX.Element; text: string }
                    const items: HistItem[] = []
                    if (b.createdAt) items.push({ key:'created', at:b.createdAt, icon:<PlusCircle size={12} className="text-gray-400"/>, text:`สร้างรายการ เมื่อ ${fmtDT(b.createdAt)}` })
                    if (b.approvedAt) items.push({ key:'approved', at:b.approvedAt, icon:<CheckCircle2 size={12} className="text-green-500"/>, text:`อนุมัติเมื่อ ${fmtDT(b.approvedAt)}${b.approvedBy ? ` โดย ${b.approvedBy}` : ''}` })
                    if (b.rejectedAt) items.push({ key:'rejected', at:b.rejectedAt, icon:<XCircle size={12} className="text-red-500"/>, text:`ปฏิเสธเมื่อ ${fmtDT(b.rejectedAt)}${b.rejectedBy ? ` โดย ${b.rejectedBy}` : ''}` })
                    ;(b.logs||[]).forEach(l => {
                      if (l.action === 'EXTENDED' || l.action === 'ADJUSTED') {
                        const verb = l.action === 'EXTENDED' ? 'ขยายเวลาคืน' : 'ปรับเวลาคืน'
                        items.push({ key:l.id, at:l.createdAt, icon:<CalendarClock size={12} className="text-amber-500"/>,
                          text:`${verb}จาก ${l.oldValue?fmtDT(l.oldValue):'-'} เป็น ${l.newValue?fmtDT(l.newValue):'-'}${l.actor ? ` โดย ${l.actor}` : ''}` })
                      }
                    })
                    if (b.returnedAt) items.push({ key:'returned', at:b.returnedAt, icon:<RotateCcw size={12} className="text-blue-500"/>, text:`รับคืนเมื่อ ${fmtDT(b.returnedAt)}${b.returnedBy ? ` โดย ${b.returnedBy}` : ''}` })
                    items.sort((a,b2) => new Date(a.at).getTime() - new Date(b2.at).getTime())
                    return (
                      <div className="mt-2 pl-2 border-l-2 border-gray-100 space-y-1.5">
                        {items.map(it => (
                          <div key={it.key} className="flex items-center gap-1.5 text-xs text-gray-500">{it.icon} {it.text}</div>
                        ))}
                      </div>
                    )
                  })()}
                </div>
                <div className="flex gap-2 items-start">
                  {isAdmin && b.status === 'PENDING' && (
                    <>
                      <button onClick={() => statusMut.mutate({id:b.id,status:'APPROVED'})} className="flex items-center gap-1 px-3 py-1.5 bg-green-600 text-white rounded-lg text-xs hover:bg-green-700"><Check size={12}/> อนุมัติ</button>
                      <button onClick={() => statusMut.mutate({id:b.id,status:'REJECTED'})} className="flex items-center gap-1 px-3 py-1.5 bg-red-100 text-red-700 rounded-lg text-xs hover:bg-red-200"><X size={12}/> ปฏิเสธ</button>
                    </>
                  )}
                  {isAdmin && b.status === 'APPROVED' && (
                    <button onClick={() => statusMut.mutate({id:b.id,status:'RETURNED'})} className="flex items-center gap-1 px-3 py-1.5 bg-blue-100 text-blue-700 rounded-lg text-xs hover:bg-blue-200"><Check size={12}/> รับคืนแล้ว</button>
                  )}
                  {(b.status === 'PENDING' || b.status === 'APPROVED') && (
                    <button onClick={() => openExtend(b)} className="flex items-center gap-1 px-3 py-1.5 bg-amber-50 text-amber-700 rounded-lg text-xs hover:bg-amber-100"><CalendarClock size={12}/> ขยายเวลา</button>
                  )}
                  {(isAdmin || b.status === 'PENDING') && (
                    <button onClick={() => { if(window.confirm('ลบรายการยืมนี้?')) deleteMut.mutate(b.id) }} className="p-1.5 hover:bg-red-50 rounded-lg"><Trash2 size={14} className="text-red-400"/></button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* New Borrow Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setShowForm(false)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-5 border-b border-gray-100 shrink-0">
              <h2 className="font-bold text-gray-900">ยืม Asset</h2>
              <button onClick={() => setShowForm(false)}><X size={18}/></button>
            </div>
            <div className="p-5 space-y-4 overflow-y-auto">
              <div>
                {form.useCustomItem ? (
                  <div>
                    <label className="text-xs font-medium text-gray-600 mb-1 block">ชื่อสิ่งของ *</label>
                    <div className="flex gap-2">
                      <input value={form.itemName} onChange={e=>setForm(f=>({...f,itemName:e.target.value}))} className={ic} placeholder="พิมพ์ชื่อสิ่งของที่ยืม..."/>
                    </div>
                    <button type="button" onClick={() => setForm(f=>({...f,useCustomItem:false,itemName:''}))} className="text-xs text-blue-600 hover:underline mt-1">เลือกจากรายการ Asset แทน</button>
                  </div>
                ) : (
                  <div>
                    <AssetSearchSelect
                      label="Asset *"
                      value={form.assetId}
                      onChange={id => setForm(f=>({...f,assetId:id}))}
                    />
                    <button type="button" onClick={() => setForm(f=>({...f,useCustomItem:true,assetId:''}))} className="text-xs text-blue-600 hover:underline mt-1">ไม่มีใน Asset? พิมพ์ชื่อเอง</button>
                  </div>
                )}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-gray-600 mb-1 block">วันที่ยืม *</label>
                  <input type="date" value={form.startDate} onChange={e=>setForm(f=>({...f,startDate:e.target.value}))} className={ic}/>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-600 mb-1 block">เวลาที่ยืม</label>
                  <input type="time" value={form.startTime} onChange={e=>setForm(f=>({...f,startTime:e.target.value}))} className={ic}/>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-600 mb-1 block">วันคืน (กำหนด) *</label>
                  <input type="date" value={form.endDate} onChange={e=>setForm(f=>({...f,endDate:e.target.value}))} className={ic}/>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-600 mb-1 block">เวลาคืน</label>
                  <input type="time" value={form.endTime} onChange={e=>setForm(f=>({...f,endTime:e.target.value}))} className={ic}/>
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">ผู้ยืม</label>
                <input value={form.bookedBy} onChange={e=>setForm(f=>({...f,bookedBy:e.target.value}))} className={ic} placeholder="ชื่อผู้ยืม..."/>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">วัตถุประสงค์</label>
                <input value={form.purpose} onChange={e=>setForm(f=>({...f,purpose:e.target.value}))} className={ic} placeholder="ใช้งานเพื่อ..."/>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">หมายเหตุ</label>
                <textarea value={form.notes} onChange={e=>setForm(f=>({...f,notes:e.target.value}))} rows={2} className={ic} placeholder="หมายเหตุเพิ่มเติม..."/>
              </div>
              {conflict && <div className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg">{conflict}</div>}
            </div>
            <div className="flex gap-3 p-5 pt-3 border-t border-gray-100 shrink-0">
              <button onClick={() => { setShowForm(false); setConflict(null) }} className="flex-1 py-2.5 border border-gray-300 rounded-xl text-sm">ยกเลิก</button>
              <button onClick={() => createMut.mutate(form)} disabled={(!form.useCustomItem&&!form.assetId)||(form.useCustomItem&&!form.itemName.trim())||!form.startDate||!form.endDate||createMut.isPending} className="flex-1 py-2.5 bg-blue-600 text-white rounded-xl text-sm font-medium hover:bg-blue-700 disabled:opacity-50">
                {createMut.isPending ? 'กำลังตรวจสอบ...' : 'ยืนยันการยืม'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Extend Booking Modal */}
      {extendTarget && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setExtendTarget(null)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-5 border-b border-gray-100">
              <h2 className="font-bold text-gray-900 flex items-center gap-2"><CalendarClock size={18}/> ขยายเวลาการยืม</h2>
              <button onClick={() => setExtendTarget(null)}><X size={18}/></button>
            </div>
            <div className="p-5 space-y-4">
              <p className="text-sm text-gray-500">
                {extendTarget.asset?.assetCode || extendTarget.asset?.name || extendTarget.itemName} · กำหนดคืนเดิม {fmtDT(extendTarget.endDate)}
              </p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-gray-600 mb-1 block">วันคืนใหม่ *</label>
                  <input type="date" value={extendDate} onChange={e=>setExtendDate(e.target.value)} className={ic}/>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-600 mb-1 block">เวลาคืนใหม่</label>
                  <input type="time" value={extendTime} onChange={e=>setExtendTime(e.target.value)} className={ic}/>
                </div>
              </div>
              {extendError && <div className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg">{extendError}</div>}
            </div>
            <div className="flex gap-3 p-5 pt-0">
              <button onClick={() => setExtendTarget(null)} className="flex-1 py-2.5 border border-gray-300 rounded-xl text-sm">ยกเลิก</button>
              <button
                onClick={() => extendMut.mutate({ id: extendTarget.id, endDate: `${extendDate}T${extendTime}:00` })}
                disabled={!extendDate || extendMut.isPending}
                className="flex-1 py-2.5 bg-amber-600 text-white rounded-xl text-sm font-medium hover:bg-amber-700 disabled:opacity-50">
                {extendMut.isPending ? 'กำลังบันทึก...' : 'ยืนยันขยายเวลา'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
